import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2, ShieldCheck, ClipboardList } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 12; // ~30 seconds

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("checking");
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError("Missing session identifier. If you completed a payment, please contact the team on WhatsApp.");
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const res = await fetch(`${API}/coaching/checkout/status/${sessionId}`);
        if (!res.ok) throw new Error(`Status check failed (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        setPayment(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (attempts++ < MAX_POLLS) {
          setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("timeout");
        }
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setError(err.message);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const goToAssessment = () => {
    // Pass along the session and program to the assessment
    const q = new URLSearchParams();
    if (sessionId) q.set("session_id", sessionId);
    if (payment?.program_id) q.set("program_id", payment.program_id);
    navigate(`/assessment?${q.toString()}`);
  };

  if (status === "checking") {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 px-6 flex items-center justify-center"
        data-testid="payment-checking"
      >
        <div className="text-center max-w-md mx-auto">
          <Loader2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-6 animate-spin" />
          <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-3">Confirming Payment</p>
          <p className="text-white/70 text-sm leading-relaxed">
            Please wait while we verify your transaction with Stripe. This should only take a moment.
          </p>
        </div>
      </div>
    );
  }

  if (status === "failed" || status === "error" || status === "timeout") {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 px-6 flex items-center justify-center"
        data-testid="payment-error"
      >
        <div className="text-center max-w-lg mx-auto">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-red-500/60 rounded-full mb-6">
            <span className="text-red-500 text-2xl">!</span>
          </div>
          <h1
            className="font-display text-4xl text-white mb-4 leading-[0.95]"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            WE COULDN&rsquo;T CONFIRM YOUR PAYMENT
          </h1>
          <p className="text-white/70 text-sm leading-relaxed mb-8">
            {error || "The payment status hasn't been confirmed yet. If you were charged, please contact the coaching team so we can sort this out right away."}
          </p>
          <Link
            to="/#coaching-programs"
            className="inline-flex items-center justify-center gap-3 border border-white/30 text-white hover:bg-white hover:text-black px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-all"
            data-testid="payment-back-btn"
          >
            Back to Programs
          </Link>
        </div>
      </div>
    );
  }

  // PAID
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-28 sm:pt-32 pb-24 px-6" data-testid="payment-success">
      <div className="max-w-2xl mx-auto text-center">
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center justify-center w-20 h-20 bg-[#D4AF37] rounded-full mb-8"
        >
          <Check size={38} className="text-black" strokeWidth={3} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <p className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.35em] uppercase mb-4 font-bold">
            Welcome to Camp Cormier
          </p>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-[0.95] tracking-tight"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            PAYMENT COMPLETE —{" "}
            <span className="text-[#D4AF37]">YOUR NEXT STEP</span>
          </h1>

          {payment?.program_name && (
            <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-black/40 px-4 py-1.5 mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[11px] tracking-[0.25em] uppercase font-semibold">
                {payment.program_name}
              </span>
            </div>
          )}

          <p className="text-white/75 text-base sm:text-lg leading-relaxed mb-4">
            Your payment has been successfully completed.
          </p>

          <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-xl mx-auto mb-10">
            Before your coaching program begins, please complete your{" "}
            <span className="text-[#D4AF37] font-semibold">Coaching Assessment</span>. This
            allows Chris Cormier and the coaching team to understand your goals, training
            history, nutrition, lifestyle, current condition and individual needs so we can
            build your coaching strategy.
          </p>

          <button
            type="button"
            onClick={goToAssessment}
            className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-9 sm:px-12 py-4 sm:py-5 text-xs sm:text-sm tracking-[0.3em] uppercase font-bold hover:bg-white transition-all duration-300 shadow-[0_15px_45px_-10px_rgba(212,175,55,0.55)]"
            data-testid="complete-assessment-btn"
          >
            <ClipboardList size={16} />
            Complete My Coaching Assessment
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="text-white/40 text-xs tracking-widest uppercase mt-10">
            Onboarding is complete once payment + assessment are submitted.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
